// Firestore persistence, not a Vuex module — see ../firestoreDraft.js.
// Divii integration code — replaces the upstream localStorage store.
// Same load/save/clear API, backed by agreements/{agreementId}/parentingSchedule/draft.
// The migrate()/normalizeEvent() schema backfill below is upstream's, unchanged.

import firebase from 'firebase/app';
import { agreementsCollection } from '@/firebase';
import { createFirestoreDraft } from '../firestoreDraft.js';
import { rotationFromPlanner } from '@/lib/parentingSchedule/fromLegacyPlanner.js';
import { blankRotation } from '@/lib/parentingSchedule/rotationTemplates.js';
import { DAY_INDEX, MIN_PER_DAY, timeToMin } from '@/lib/parentingSchedule/weekMath';

// Normalize an event to the current schema. Backfills week-offset fields for
// legacy events by inferring wrap from day/time ordering (matches the old
// implicit-wrap behavior in rotation.js).
function normalizeEvent(e) {
	const out = {
		partner: e.partner || 'A',
		startDay: e.startDay || 'mon',
		startTime: e.startTime || '00:00',
		endDay: e.endDay || 'mon',
		endTime: e.endTime || '00:00',
		startWeekOffset: e.startWeekOffset || 0,
		endWeekOffset: e.endWeekOffset === undefined ? null : e.endWeekOffset,
		transition: e.transition === undefined ? null : e.transition,
		transitionDetails: e.transitionDetails === undefined ? '' : e.transitionDetails,
	};
	if (out.endWeekOffset === null) {
		const startPos = (DAY_INDEX[out.startDay] ?? 0) * MIN_PER_DAY + timeToMin(out.startTime);
		const endPos = (DAY_INDEX[out.endDay] ?? 0) * MIN_PER_DAY + timeToMin(out.endTime);
		out.endWeekOffset = endPos <= startPos ? 1 : 0;
	}
	return out;
}

// Migrate legacy state to the current shape. Runs on every load so older
// drafts get missing fields backfilled with sensible defaults.
export function migrate(state) {
	const next = { ...state };
	if (typeof next.rotationLength !== 'number' || next.rotationLength < 1) {
		next.rotationLength = 2;
	}
	if (!next.anchorDate) {
		next.anchorDate = blankRotation().anchorDate;
	}
	if (!Array.isArray(next.weeks)) {
		next.weeks = [];
	}
	while (next.weeks.length < next.rotationLength) {
		next.weeks.push({ events: [] });
	}
	next.weeks = next.weeks.slice(0, next.rotationLength).map((w) => ({
		events: Array.isArray(w?.events) ? w.events.map((e) => normalizeEvent(e)) : [],
	}));
	return next;
}

async function importLegacy(caseId, raw) {
	const hasEvents = !!raw?.weeks?.some((w) => w?.events?.length);
	if (hasEvents) return null;
	try {
		const plannerRef = agreementsCollection.doc(caseId).collection('planner').doc('schedule');
		const snap = await plannerRef.get();
		if (!snap.exists || snap.data().importedToParentingSchedule) return null;
		const value = rotationFromPlanner(snap.data().events, raw?.anchorDate);
		if (!value) return null;
		const markImported = () =>
			plannerRef.set(
				{ importedToParentingSchedule: firebase.firestore.FieldValue.serverTimestamp() },
				{ merge: true }
			);
		return { value, markImported };
	} catch (e) {
		console.warn('[parenting-schedule] could not read the old planner:', e);
		return null;
	}
}

const store = createFirestoreDraft({
	collection: 'parentingSchedule',
	field: 'rotation',
	migrate,
	seed: () => blankRotation(),
	isEmpty: (raw) => typeof raw !== 'object',
	importLegacy,
	log: '[parenting-schedule]',
});

export const { load, save, flushSave, clear, onError } = store;
